import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { useGetOwnOrganizations } from '@/features/api/hooks/grapevine-organization/useGetOwnOrganizations';
import { ListSection } from '@/features/list/components/ListSection';
import { OrganizationListItem } from '@/features/profile-setting/components/OrganizationListItem';
import { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { GradientBackground } from '@/shared/components/GradientBackground';
import { StateView } from '@/shared/components/state-view/StateView';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = AppNativeStackScreenProps<
  typeof ProfileSettingRoute.OrganizationOverview
>;

/**
 * Lists all organizations the current user has a role for. The backend endpoint
 * ({@code GET /grapevine/organization/own}) returns a flat list of
 * {@code ClientOrganizationProfile}, so there is no geo area drill-down here.
 */
export const OrganizationOverviewScreen: FC<Props> = ({ navigation }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-overview'
  });

  const {
    data: organizations,
    isLoading,
    refetch,
    isRefetching
  } = useGetOwnOrganizations();

  const styles = useMemo(() => makeStyles(), []);

  const handleOrganizationPress = useCallback(
    (orgId: string) => {
      navigation.navigate(ProfileSettingRoute.OrganizationDetail, {
        id: orgId
      });
    },
    [navigation]
  );

  const hasOrganizations = (organizations?.length ?? 0) > 0;

  return (
    <GradientBackground>
      <StateView
        isLoading={isLoading}
        isRefetching={isRefetching}
        onRefetch={refetch}
        stateLabels={t('state', { returnObjects: true })}
        backgroundColor="transparent"
        contentContainerStyle={styles.content}
      >
        {hasOrganizations ? (
          <ListSection title={t('organizations')}>
            {organizations?.map((org) => (
              <OrganizationListItem
                key={org.id}
                organization={org}
                onPress={() => handleOrganizationPress(org.id)}
                backgroundColor={theme.colors.surfaceContainerLowest}
              />
            ))}
          </ListSection>
        ) : (
          <View style={styles.emptyContainer}>
            <Text variant="bodyLarge">{t('empty')}</Text>
          </View>
        )}
      </StateView>
    </GradientBackground>
  );
};

const makeStyles = () =>
  StyleSheet.create({
    content: { paddingBottom: 32 },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 32
    }
  });
